/** @jsx jsx */
import {
  Link,
  Title,
  Label,
  Box,
  Col,
  Container,
} from '@bottlebooks/gatsby-theme-base';
import { Trans } from '@lingui/macro';
import { graphql as gatsbyGraphQL } from 'gatsby';
import { jsx } from 'theme-ui';
import useLink from '@bottlebooks/gatsby-theme-event/src/useLink';
import BrandAddressSection from '@bottlebooks/gatsby-theme-event/src/components/Brand/BrandAddressSection';
import BrandBanner from '@bottlebooks/gatsby-theme-event/src/components/Brand/BrandBanner';
import BrandShopsSection from '@bottlebooks/gatsby-theme-event/src/components/Brand/BrandShopsSection';
import ContentRow from '@bottlebooks/gatsby-theme-event/src/components/ContentRow';
import ExhibitorActions from '@bottlebooks/gatsby-theme-event/src/components/Exhibitor/ExhibitorActions';
import Layout from '@bottlebooks/gatsby-theme-event/src/components/Layout';
import ProductCard from '@bottlebooks/gatsby-theme-event/src/components/ProductCard/ProductCard';
import ProductsList from '@bottlebooks/gatsby-theme-event/src/components/ProductsList/ProductsList';
import SEO from '@bottlebooks/gatsby-theme-event/src/components/seo.js';
import ExhibitorHeaderRow from '@bottlebooks/gatsby-theme-event/src/components/ExhibitorPage/ExhibitorHeaderRow';
import BrandDescription from '@bottlebooks/gatsby-theme-event/src/components/Brand/BrandDescription';
import BrandCertifications from '@bottlebooks/gatsby-theme-event/src/components/Brand/BrandCertifications';
import FeaturedBrand from '@bottlebooks/gatsby-theme-event/src/components/Brand/FeaturedBrand';
import BrandGallery from '@bottlebooks/gatsby-theme-event/src/components/Brand/BrandGallery';
import BrandVideos from '@bottlebooks/gatsby-theme-event/src/components/Brand/BrandVideos';
import BrandContacts from '@bottlebooks/gatsby-theme-event/src/components/Brand/BrandContacts';
import React from 'react';

export default function PavilionPage({
  exhibitor,
  products,
  previous,
  location,
  next,
  locale,
}) {
  const link = useLink();
  const { visibleProducers, hasFilter, visibleProducts } = useAfterQuery(
    exhibitor,
    products,
    location
  );
  const hasProducts = Boolean(visibleProducts?.length);
  const page = {
    path: location.href,
    title: exhibitor.name,
    image: exhibitor.logo?.fixed?.src || '',
    description: exhibitor.shortDescription,
  };

  return (
    <Layout locale={locale} sx={{ backgroundColor: 'transparent' }}>
      <SEO page={page} />
      <BrandBanner {...exhibitor} />
      <ExhibitorHeaderRow
        exhibitor={exhibitor}
        previous={previous}
        next={next}
      />

      <Container>
        <ContentRow sx={{ paddingY: 3 }}>
          <Col>
            <ExhibitorActions exhibitor={exhibitor} />
          </Col>
        </ContentRow>
      </Container>

      <Container>
        <ContentRow>
          <Col flex>
            <BrandDescription brand={exhibitor} />
            <BrandCertifications product={exhibitor} />
          </Col>
        </ContentRow>
      </Container>

      <Container>
        <ContentRow>
          <Col flex>
            <BrandGallery brand={exhibitor} />
            <BrandVideos brand={exhibitor} />
            <BrandContacts brand={exhibitor} />
          </Col>
        </ContentRow>
      </Container>

      <Container>
        <ContentRow sx={{ paddingY: 3 }}>
          <Col flex>
            <FeaturedBrands
              producers={visibleProducers}
              hasFilter={hasFilter}
              location={location}
            />
          </Col>
        </ContentRow>
      </Container>

      {hasProducts ? (
        <Container fluid sx={{ maxWidth: 'container.fluid' }}>
          <Title sx={{ paddingY: 4 }}>
            <Trans>Products</Trans>
          </Title>

          {hasProducts && (
            <ProductsList
              count={visibleProducts.length}
              sx={{ marginX: [-3, -2], marginY: -2 }}
            >
              {visibleProducts.map((product) => (
                <ProductCard
                  key={product.id}
                  product={product}
                  to={link.product(product)}
                  sx={{ marginBottom: [null, null, 3] }}
                />
              ))}
            </ProductsList>
          )}
        </Container>
      ) : null}
      <BrandAddressSection brand={exhibitor} />

      <Container>
        <ContentRow>
          <Col>
            <BrandShopsSection brand={exhibitor} />
          </Col>
        </ContentRow>
      </Container>
    </Layout>
  );
}

function FeaturedBrands({ producers, hasFilter, location, ...rest }) {
  if (!producers?.length) return null;
  return (
    <Box id="brands" {...rest}>
      <Title sx={{ marginBottom: 3 }}>
        <Trans>Presented producers</Trans>
        {hasFilter && (
          <Label sx={{ display: 'inline-block', marginLeft: 2 }}>
            {' '}
            <Link to={`${location.pathname}#brands`}>
              <Trans>Show all</Trans>
            </Link>
          </Label>
        )}
      </Title>
      <Box sx={{ border: 1, borderColor: 'border' }}>
        {producers?.map(({ profile, brandId }) => (
          <FeaturedBrand
            key={brandId}
            brand={profile}
            to={
              hasFilter
                ? `${location.pathname}#brands`
                : `${location.pathname}#${brandId}`
            }
          />
        ))}
      </Box>
    </Box>
  );
}

export const fragment = gatsbyGraphQL/* GraphQL */ `
  fragment vv_PavilionPage on Exhibitor {
    ...BrandBanner
    ...ExhibitorHeaderRow
    products {
      ...vv_PavilionPage_Product
      ...useLink_Product
    }
  }

  fragment vv_PavilionPage_RegisteredBrand on Bottlebooks_RegisteredBrand {
    brandId
    ...bb_RegisteredBrand_BrandLogoDefault
    profile {
      name
      countryName: country(format: LOCALIZED)
      region
      mainImage {
        fluid(maxWidth: 660, maxHeight: 300, crop: FILL, gravity: FACES_AUTO) {
          src
          srcSet
          sizes
          base64
          aspectRatio
        }
      }
    }
  }

  fragment vv_PavilionPage_Registration on Bottlebooks_SingleRegistration {
    # For now, we have to cheat because registration.producers isn't implemented yet.
    collection {
      registeredBrands(filter: { companyId: { eq: $exhibitorId } }) {
        nodes {
          ...vv_PavilionPage_RegisteredBrand
        }
      }
    }
    #producers {
    #  nodes {
    #    ...vv_PavilionPage_RegisteredBrand
    #  }
    #}
    registeredProducts {
      nodes {
        producerId
      }
    }
  }

  fragment vv_bb_ExhibitorPage on Bottlebooks_AbstractBrand {
    ...bb_ExhibitorActions
    ...bb_ExhibitorSections
    ...bb_BrandAddressSection
    ...bb_BrandShopsSection
    ... on Bottlebooks_Exhibitor {
      products {
        nodes {
          ...bb_ProductListRow
          ...bb_ProductCard
          ...useLink_bb_Product
        }
      }
    }
  }

  fragment vv_PavilionPage_Product on Product {
    id
    producerId
    ...ProductListRow
    ...ProductCard
    ...useLink_Product
  }
`;

function useAfterQuery(exhibitor, products, location) {
  const registeredBrands = exhibitor?.collection?.registeredBrands?.nodes;
  const filter = location.hash?.replace('#', '');
  const filteredProducers = registeredBrands.filter(
    (brand) => brand.brandId === filter
  );
  const filteredProducts = products.filter(
    (product) => product.producerId === filter
  );
  const hasFilter = Boolean(filter && filteredProducers.length);
  const visibleProducers = hasFilter ? filteredProducers : registeredBrands;
  const visibleProducts = hasFilter ? filteredProducts : products;
  return {
    ...exhibitor,
    registeredBrands,
    visibleProducers,
    filter,
    hasFilter,
    visibleProducts,
  };
}
